import type { CSSProperties } from "react";
import { cn } from "@/shared/cn";
import { TYPE_SCALE_PX } from "@/shared/ui/type-scale";

type AvatarSize = "xs" | "sm" | "md" | "lg";
type AvatarShape = "rounded" | "rounded-md";
type AvatarTone = "plain" | "soft" | "solid" | "mono";

export interface AvatarProps {
  name: string;
  size: AvatarSize;
  shape?: AvatarShape;
  tone?: AvatarTone;
  colourKey?: string;
}

export function Avatar({ name, size, shape = "rounded", tone = "plain", colourKey }: AvatarProps) {
  const px = SIZE_PX[size];
  const letters = tone === "plain" || tone === "mono" ? initials(name, 1) : initials(name, 2);
  const paint =
    colourKey === undefined
      ? null
      : avatarToneProps(colourKey, tone === "solid" ? "solid" : "soft");
  return (
    <span
      role="img"
      aria-label={name}
      data-tone={paint?.["data-tone"]}
      className={cn(
        "inline-flex shrink-0 items-center justify-center leading-none",
        tone === "plain" ? SHAPE_CLASS[shape] : TONE_RADIUS[size],
        TONE_CLASS[tone],
      )}
      style={{ width: px, height: px, fontSize: TEXT_SIZE_PX[size], ...paint?.style }}
    >
      {letters}
    </span>
  );
}

export function initials(name: string, count: 1 | 2): string {
  return name
    .trim()
    .split(/\s+/)
    .map((word) => word[0] ?? "")
    .slice(0, count)
    .join("")
    .toUpperCase();
}

export function avatarToneProps(
  colourKey: string,
  look: "solid" | "soft",
): { "data-tone": number; style: CSSProperties } {
  const tone = avatarToneOf(colourKey);
  const colour = `var(--avatar-${tone + 1})`;
  return {
    "data-tone": tone,
    style:
      look === "solid"
        ? { background: colour }
        : {
            background: `color-mix(in srgb, ${colour} 14%, transparent)`,
            color: `var(--avatar-${tone + 1}-ink)`,
          },
  };
}

export function avatarToneOf(colourKey: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < colourKey.length; index += 1) {
    hash ^= colourKey.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) % AVATAR_TONE_COUNT;
}

export const AVATAR_TONE_COUNT = 6;

const SIZE_PX: Record<AvatarSize, number> = { xs: 22, sm: 24, md: 28, lg: 40 };
const TEXT_SIZE_PX: Record<AvatarSize, number> = {
  xs: TYPE_SCALE_PX.xs,
  sm: TYPE_SCALE_PX.xs,
  md: TYPE_SCALE_PX.xs,
  lg: TYPE_SCALE_PX.md,
};

const SHAPE_CLASS: Record<AvatarShape, string> = {
  rounded: "rounded-[var(--radius-sm)]",
  "rounded-md": "rounded-[var(--radius-md)]",
};

const TONE_RADIUS: Record<AvatarSize, string> = {
  xs: "rounded-[6px]",
  sm: "rounded-[7px]",
  md: "rounded-[9px]",
  lg: "rounded-[12px]",
};

const TONE_CLASS: Record<AvatarTone, string> = {
  plain:
    "bg-[var(--color-light-02)] text-[var(--color-text-muted)] font-medium font-(family-name:--sans)",
  soft: "bg-(--brand-soft) text-(--brand) font-semibold",
  solid: "bg-(--brand) text-white font-semibold",
  mono: "bg-(--fill) text-(--muted) font-(family-name:--mono)",
};
